// TaskForm.jsx: Form for creating and editing task details.
// @ts-check
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import { useProjectLimitGuard } from '@/features/wall';
import { useTaskFormState } from '@/features/tasks/hooks/form/use-task-form.js';
import useAddSubtask from '@/features/tasks/hooks/mutations/use-task-add-subtask.js';
import { TASK_FORM_HEADER_GAP } from '@/features/tasks/components/forms/taskFormConstants';
import { useTaskFormPendingSubtasks } from '@/features/tasks/components/forms/useTaskFormPendingSubtasks';
import { useDeviceClass } from '@/hooks/ui/useBreakpoint';
import { TaskFormActions } from '@/features/tasks/components/forms/TaskFormActions';
import { TaskFormScrollBody } from '@/features/tasks/components/forms/TaskFormScrollBody';
import { submitTaskFormWithTags } from '@/features/tasks/components/forms/taskFormSubmitHelpers';

/**
 * @param {{
 *   initialTask?: Task,
 *   handleSubmit: (payload: UpdateTask | NewTask) => Promise<void>,
 *   onClose?: () => void,
 *   tasks?: Task[],
 *   onUpdate?: (task: Task) => void,
 *   titleInHeader?: boolean,
 *   headerTitle?: string,
 *   onHeaderTitleChange?: (title: string) => void,
 * }} props
 */
const TaskForm = ({
  initialTask,
  handleSubmit,
  onClose,
  tasks,
  onUpdate,
  titleInHeader = false,
  headerTitle = '',
  onHeaderTitleChange: _onHeaderTitleChange,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { guardProjects } = useProjectLimitGuard();
  const { tasksMap, projectDisplayMap, contextDisplayMap } = useTasksContext();
  const { deleteTask } = useTaskMutationsContext();
  const { isBattlefield, isPhone } = useDeviceClass();
  const pinFormActions = titleInHeader && isPhone;
  const { handleSaveSubtask } = useAddSubtask(initialTask, tasks, onUpdate);
  const [isDeleting, setIsDeleting] = useState(false);

  const isNewTask = !initialTask?.id;

  const {
    task,
    errors,
    isSubmitting,
    isRollup,
    isRecurrenceEnabled,
    setIsRecurrenceEnabled,
    recurrenceInterval,
    setRecurrenceInterval,
    recurrenceFrequency,
    setRecurrenceFrequency,
    subtasks,
    calculatedDuration,
    options: {
      parentTaskOptions,
      projectOptions,
      contextOptions,
      personOptions,
    },
    handlers: {
      handleChange,
      handleDescriptionChange,
      handleParentChange,
      handleTimeChange,
      handleDurationChange,
      handleProjectsChange,
      handleContextsChange,
      handlePersonChange,
      onFormSubmit,
    },
  } = useTaskFormState(initialTask, tasks, async (taskPayload) =>
    submitTaskFormWithTags({
      taskPayload,
      user,
      guardProjects,
      projectDisplayMap,
      contextDisplayMap,
      isNewTask,
      pendingSubtasks,
      handleSubmit,
    }),
  );

  const {
    pendingSubtasks,
    handleAddPendingSubtask,
    handleTogglePendingSubtaskComplete,
  } = useTaskFormPendingSubtasks(task);

  useEffect(() => {
    if (titleInHeader && headerTitle !== task.title) {
      handleChange({ target: { name: 'title', value: headerTitle } });
    }
  }, [titleInHeader, headerTitle, task.title, handleChange]);

  const handleCancel = () => {
    if (onClose) onClose();
    else navigate('/app');
  };

  const handleDelete = async () => {
    if (!initialTask?.id) return;
    setIsDeleting(true);
    try {
      await deleteTask(initialTask.id);
      if (onClose) onClose();
    } catch (error) {
      console.error('Failed to delete task:', error);
      setIsDeleting(false);
    }
  };

  const subtaskRows = isNewTask ? pendingSubtasks : subtasks;

  return (
    <Box
      component="form"
      onSubmit={/** @type {any} */ (onFormSubmit)}
      autoComplete="off"
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: titleInHeader ? TASK_FORM_HEADER_GAP : 2,
        ...(pinFormActions
          ? { flex: 1, minHeight: 0, gap: 0 }
          : {}),
      }}
    >
      <TaskFormScrollBody
        pinFormActions={pinFormActions}
        titleInHeader={titleInHeader}
        task={task}
        errors={errors}
        handleChange={handleChange}
        handleDescriptionChange={handleDescriptionChange}
        tagRowProps={{
          task,
          projectOptions,
          contextOptions,
          personOptions,
          parentTaskOptions,
          tasksMap,
          projectDisplayMap,
          contextDisplayMap,
          handleProjectsChange,
          handleContextsChange,
          handlePersonChange,
          handleParentChange,
          handleChange,
          titleInHeader,
        }}
        dateTimeFieldProps={{
          task,
          errors,
          isRollup,
          calculatedDuration,
          isRecurrenceEnabled,
          setIsRecurrenceEnabled,
          recurrenceInterval,
          setRecurrenceInterval,
          recurrenceFrequency,
          setRecurrenceFrequency,
          handleChange,
          handleTimeChange,
          handleDurationChange,
        }}
        isNewTask={isNewTask}
        initialTaskId={initialTask?.id}
        subtaskRows={subtaskRows}
        onToggleSubtaskComplete={(st, completed) => {
          if (isNewTask)
            handleTogglePendingSubtaskComplete(st.localId, completed);
          else handleSaveSubtask({ ...st, completed });
        }}
        onAddPendingSubtask={handleAddPendingSubtask}
        onSaveSubtask={handleSaveSubtask}
      />

      <TaskFormActions
        isBattlefield={isBattlefield}
        pinToViewport={pinFormActions}
        showDelete={Boolean(initialTask?.id)}
        isDeleting={isDeleting}
        onDelete={handleDelete}
        onCancel={handleCancel}
        showSubmit={Boolean(handleSubmit)}
        isSubmitting={isSubmitting}
        isEdit={Boolean(initialTask?.id)}
      />
    </Box>
  );
};

export default TaskForm;
